"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
/**
 * LoginPage component for user authentication
 */
const react_1 = require("react");
const react_router_dom_1 = require("react-router-dom");
const AuthProvider_1 = require("../providers/AuthProvider");
require("../../styles/pages/loginPage.scss");
/**
 * LoginPage component displays the login form
 * @returns {React.FC} The LoginPage component
 */
const LoginPage = () => {
    const navigate = (0, react_router_dom_1.useNavigate)();
    const location = (0, react_router_dom_1.useLocation)();
    const { login, isAuthenticated, loading, error } = (0, AuthProvider_1.useAuth)();
    const [username, setUsername] = (0, react_1.useState)('');
    const [password, setPassword] = (0, react_1.useState)('');
    const [localError, setLocalError] = (0, react_1.useState)(null);
    const [loginAttempts, setLoginAttempts] = (0, react_1.useState)(0);
    const [lockoutUntil, setLockoutUntil] = (0, react_1.useState)(null);
    /**
     * Redirect to intended destination (or home) if already authenticated
     */
    (0, react_1.useEffect)(() => {
        var _a;
        if (isAuthenticated) {
            const fromLoc = (_a = location.state) === null || _a === void 0 ? void 0 : _a.from;
            const from = fromLoc ? `${fromLoc.pathname}${fromLoc.search || ''}` : '/';
            navigate(from, { replace: true });
        }
    }, [isAuthenticated, navigate, location]);
    /**
     * Handle form submission with validation and rate limiting
     */
    const handleSubmit = (0, react_1.useCallback)((e) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        e.preventDefault();
        setLocalError(null);
        // Check client-side rate limiting lockout
        if (lockoutUntil && Date.now() < lockoutUntil) {
            const secondsRemaining = Math.ceil((lockoutUntil - Date.now()) / 1000);
            setLocalError(`Too many failed attempts. Please wait ${secondsRemaining} seconds.`);
            return;
        }
        // Trim whitespace from inputs
        const trimmedUsername = username.trim();
        const trimmedPassword = password.trim();
        // Basic validation
        if (!trimmedUsername || !trimmedPassword) {
            setLocalError('Please enter both username and password');
            return;
        }
        // Length validation
        if (trimmedUsername.length < 3 || trimmedUsername.length > 100) {
            setLocalError('Username must be between 3 and 100 characters');
            return;
        }
        if (trimmedPassword.length < 8 || trimmedPassword.length > 128) {
            setLocalError('Password must be between 8 and 128 characters');
            return;
        }
        // Character validation (allows email-valid characters)
        if (!/^[a-zA-Z0-9._@+-]+$/.test(trimmedUsername)) {
            setLocalError('Username contains invalid characters');
            return;
        }
        try {
            yield login(trimmedUsername, trimmedPassword);
            setLoginAttempts(0); // Reset on success
            setLockoutUntil(null);
            const fromLoc = (_a = location.state) === null || _a === void 0 ? void 0 : _a.from;
            const from = fromLoc ? `${fromLoc.pathname}${fromLoc.search || ''}` : '/';
            navigate(from, { replace: true });
        }
        catch (err) {
            // Increment failed attempts
            const newAttempts = loginAttempts + 1;
            setLoginAttempts(newAttempts);
            // Apply exponential backoff after 5 attempts
            if (newAttempts >= 5) {
                const lockoutSeconds = 60 * Math.pow(2, Math.floor(newAttempts / 5) - 1);
                const lockoutTime = Date.now() + (lockoutSeconds * 1000);
                setLockoutUntil(lockoutTime);
                setLocalError(`Too many failed attempts. Please wait ${lockoutSeconds} seconds before trying again.`);
            }
            else {
                // Display error message from API or generic message
                const errorMessage = err instanceof Error ? err.message : 'Invalid username or password';
                setLocalError(errorMessage);
            }
        }
    }), [username, password, login, navigate, loginAttempts, lockoutUntil]);
    return ((0, jsx_runtime_1.jsx)("div", { className: "login-page", children: (0, jsx_runtime_1.jsxs)("div", { className: "login-container", children: [(0, jsx_runtime_1.jsx)("h1", { className: "login-title", children: "DegreePlan" }), (0, jsx_runtime_1.jsx)("p", { className: "login-subtitle", children: "Sign in to continue" }), (0, jsx_runtime_1.jsxs)("form", { className: "login-form", onSubmit: handleSubmit, children: [(error || localError) && ((0, jsx_runtime_1.jsx)("div", { className: "login-error", role: "alert", children: error || localError })), (0, jsx_runtime_1.jsxs)("div", { className: "form-group", children: [(0, jsx_runtime_1.jsx)("label", { htmlFor: "username", className: "form-label", children: "Username" }), (0, jsx_runtime_1.jsx)("input", { id: "username", type: "text", className: "form-input", value: username, onChange: (e) => setUsername(e.target.value), disabled: loading, autoComplete: "email", required: true })] }), (0, jsx_runtime_1.jsxs)("div", { className: "form-group", children: [(0, jsx_runtime_1.jsx)("label", { htmlFor: "password", className: "form-label", children: "Password" }), (0, jsx_runtime_1.jsx)("input", { id: "password", type: "password", className: "form-input", value: password, onChange: (e) => setPassword(e.target.value), disabled: loading, autoComplete: "current-password", required: true })] }), (0, jsx_runtime_1.jsx)("button", { type: "submit", className: "login-button", disabled: loading, "aria-label": "Sign in to DegreePlan", children: loading ? 'Signing in...' : 'Sign In' })] })] }) }));
};
exports.default = LoginPage;
